import { bulkRemoveEntities } from '@/backend/bulkRemoveEntities'
import * as getBackend from '@/backend/getEntity'
import * as listEntitiesBackend from '@/backend/listEntities'
import * as listFilteredEntitiesBackend from '@/backend/listFilteredEntities'
import type { APIError, APIResult, EntityResponse } from '@/backend/types'
import { useEntityStore } from '@/data/useEntityStore'
import { queryClient } from '@/modules/App/queryClient'
import type { GroupFilter } from '@/modules/Project/Filters/types'
import { type Entity } from '@/modules/Project/types'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

const ONE_SECOND_MS = 1000

export const useEntityClient = () => {
  const entityStore = useEntityStore()

  const invokeEntityQuery = async (params: {
    workspaceId: string
    projectId: string
    entityId: string
    staleTimeMs?: number
  }) => {
    const { workspaceId, projectId, entityId, staleTimeMs = 10 * ONE_SECOND_MS } = params
    return queryClient.fetchQuery({
      queryKey: ['entity', entityId],
      queryFn: async () => {
        const result = await getBackend.getEntity(workspaceId, projectId, entityId)
        if (result.ok) {
          return result.data
        }
        throw new Error(result.error.message, { cause: result.error })
      },
      staleTime: staleTimeMs,
    })
  }

  /**
   * Intended to be temporary,
   * reason being some pieces of code expect a JSON response.
   *
   * Fetches entity from cache or from backend and returns the raw JSON
   *
   * @deprecated We want to eliminate the use for this function
   */
  const fetchEntityJSON = async (params: {
    workspaceId: string
    projectId: string
    entityId: string
    staleTimeMs?: number
  }): Promise<APIResult<EntityResponse>> => {
    try {
      const json = await invokeEntityQuery(params)

      entityStore.pushEntityJSON(json)

      return { ok: true, data: json }
    } catch (e) {
      return { ok: false, error: (e as Error).cause as APIError }
    }
  }

  /**
   * Fetches entity from cache or from backend and returns the serialized entity
   *
   * @deprecated use useEntityQuery instead
   */
  const fetchEntity = async (params: {
    workspaceId: string
    projectId: string
    entityId: string
    staleTimeMs?: number
  }): Promise<APIResult<Entity>> => {
    try {
      const json = await invokeEntityQuery(params)

      const entity = entityStore.pushEntityJSON(json)

      return { ok: true, data: entity }
    } catch (e) {
      return { ok: false, error: (e as Error).cause as APIError }
    }
  }

  const listEntities = async (params: {
    workspaceId: string
    projectId: string
    page: { start: number; end: number }
    parentEntityId?: string
    viewId?: string
    filter?: GroupFilter
    order?: { by: string[]; direction: ('asc' | 'desc')[] }
  }) => {
    const { start, end } = params.page
    const limit = start === end ? undefined : end - start
    const offset = start

    if (params.filter) {
      const result = await listFilteredEntitiesBackend.listFilteredEntities({
        filter: params.filter,
        limit,
        offset,
        order_by: params.order?.by,
        order_directions: params.order?.direction,
        projectId: params.projectId,
        workspaceId: params.workspaceId,
      })

      if (result.ok) {
        result.data.data.forEach((entity) => {
          entityStore.pushPartialEntityJSON(entity)
          queryClient.setQueryData<EntityResponse>(['partial-entity', entity.id], entity)
        })
      }

      return result
    } else {
      const result = await listEntitiesBackend.listEntities({
        limit,
        offset,
        order_by: params.order?.by,
        order_directions: params.order?.direction,
        parent_entity_id: params.parentEntityId,
        projectId: params.projectId,
        viewId: params.viewId,
        workspaceId: params.workspaceId,
      })

      if (result.ok) {
        result.data.data.forEach((entity) => {
          entityStore.pushPartialEntityJSON(entity)
          queryClient.setQueryData<EntityResponse>(['partial-entity', entity.id], entity)
        })
      }

      return result
    }
  }

  /**
   * Pushes entity JSON to the entity store and updates the cache
   */
  const pushEntityJSON = (json: EntityResponse): Entity => {
    queryClient.setQueryData<EntityResponse>(['entity', json.id], json)
    return entityStore.pushEntityJSON(json)
  }

  /**
   * Removes entities from the cache and the entity store
   */
  const removeEntities = (entityIds: string[]) =>
    entityIds.forEach((entityId) => {
      queryClient.removeQueries({ queryKey: ['entity', entityId] })
      entityStore.removeEntities(entityIds)
    })

  function fetchAll(params: {
    workspaceId: MaybeRefOrGetter<string>
    agentId: MaybeRefOrGetter<string>
  }) {
    return useQuery({
      queryKey: [params.agentId, 'entities'],
      staleTime: 2_000,
      enabled: () => !!toValue(params.agentId),
      queryFn: async () => {
        const agentId = toValue(params.agentId)
        const workspaceId = toValue(params.workspaceId)
        const result = await listEntitiesBackend.listEntities({
          limit: 1000,
          offset: 0,
          projectId: agentId,
          workspaceId,
        })
        if (!result.ok) {
          throw new Error(result.error.message)
        }
        for (const item of result.data.data) {
          entityStore.pushEntityJSON(item)
        }

        return computed(() => entityStore.findEntitiesForAgent(agentId))
      },
    })
  }

  function deleteMutation() {
    return useMutation({
      mutationFn: async (params: {
        entityIds: string[]
        workspaceId: string
        projectId: string
      }) => {
        const result = await bulkRemoveEntities(
          params.workspaceId,
          params.projectId,
          params.entityIds,
        )

        if (!result.ok) throw new Error(result.error.message)

        removeEntities(params.entityIds)
        invalidateAgentEntities(params.projectId)
      },
    })
  }

  function invalidateAgentEntities(agentId: string) {
    queryClient.invalidateQueries({ queryKey: [agentId, 'entities'] })
  }

  return {
    fetchEntity,
    fetchEntityJSON,
    pushEntityJSON,
    removeEntities,
    listEntities,
    fetchAll,
    invalidateAgentEntities,
    deleteMutation,
  }
}
